import React, { useCallback, useEffect, useState } from "react";
import "./Gym.css";

import gym01 from '../Images/gym-01.jpg';
import gym02 from '../Images/gym-02.jpg';
import gym03 from '../Images/gym-03.jpg';
import gym04 from '../Images/gym-04.jpg';
import gym05 from '../Images/gym-05.jpg';
import gym06 from '../Images/gym-06.jpg';
import gym07 from '../Images/gym-07.jpg';
import gym08 from '../Images/gym-08.jpg';
import gym09 from '../Images/gym-09.jpg';
import gym10 from '../Images/gym-10.jpg';
import gym11 from '../Images/gym-11.jpg';

const photos = [
  { src: gym01, size: "wide", alt: "Dumbbell rack with the training floor and flags beyond" },
  { src: gym02, size: "tall", alt: "The nutrition and supplement shelves at High Street Body Shop" },
  { src: gym03, size: "normal", alt: "The training floor lit up in the evening" },
  { src: gym04, size: "normal", alt: "A plate-loaded leg press machine" },
  { src: gym05, size: "tall", alt: "Close-up of weight plates on a storage rack" },
  { src: gym06, size: "normal", alt: "Cable machine with American flag on the wall" },
  { src: gym07, size: "wide", alt: "Squat rack against a reclaimed wood wall" },
  { src: gym08, size: "normal", alt: "Row of cable machines near the entrance" },
  { src: gym09, size: "normal", alt: "Wide view of the training floor and dumbbell rack" },
  { src: gym10, size: "tall", alt: "Dumbbell rack in front of a wood-paneled wall" },
  { src: gym11, size: "feature", alt: "Full view down the High Street Body Shop training floor" },
];

export default function Gym() {
  const [activeIndex, setActiveIndex] = useState(null);

  const close = useCallback(() => setActiveIndex(null), []);
  const showPrev = useCallback(
    () => setActiveIndex((i) => (i === null ? i : (i - 1 + photos.length) % photos.length)),
    []
  );
  const showNext = useCallback(
    () => setActiveIndex((i) => (i === null ? i : (i + 1) % photos.length)),
    []
  );

  useEffect(() => {
    if (activeIndex === null) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeIndex, close, showPrev, showNext]);

  return (
    <main className="Gym">
      <p className="section-eyebrow">Our Training Space</p>
      <h1>High Street Body Shop</h1>
      <hr />
      <div className="gym-intro">
        <div className="gym-intro-card">
          <span className="gym-intro-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6.5 9v6M4 10v4M17.5 9v6M20 10v4" />
              <path d="M6.5 12h11" strokeWidth="3" />
            </svg>
          </span>
          <h2>State-of-the-Art Equipment</h2>
          <p>
            Most physical therapy facilities are limited in their capabilities because they
            don't have the space or the proper equipment to take your recovery and performance
            to the next level. At High Street Body Shop, that's not the case — our 18,000-square-foot
            facility gives you access to professional tools for strength training, functional
            movement, and conditioning.
          </p>
          <div className="gym-intro-badges">
            <span className="gym-intro-badge">18,000 sq ft</span>
            <span className="gym-intro-badge">Prime Fitness</span>
            <span className="gym-intro-badge">Hammer Strength</span>
            <span className="gym-intro-badge">Life Fitness</span>
          </div>
        </div>

        <div className="gym-intro-card">
          <span className="gym-intro-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12h4l2 8 4-16 2 8h6" />
            </svg>
          </span>
          <h2>Where Rehab Meets Performance</h2>
          <p>
            This state-of-the-art environment allows us to bridge the gap between rehabilitation
            and high-performance training. Whether you're recovering from an injury, building
            strength, or striving for peak athletic performance, our space and equipment ensure
            you have everything you need to succeed.
          </p>
        </div>
      </div>

      <div className="gym-gallery">
        {photos.map((photo, i) => (
          <button
            type="button"
            className={`gym-photo gym-photo-${photo.size}`}
            key={i}
            onClick={() => setActiveIndex(i)}
            aria-label={`View larger photo: ${photo.alt}`}
          >
            <img src={photo.src} alt={photo.alt} loading="lazy" />
            <span className="gym-photo-zoom" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                <line x1="11" y1="8" x2="11" y2="14" />
                <line x1="8" y1="11" x2="14" y2="11" />
              </svg>
            </span>
          </button>
        ))}
      </div>

      {activeIndex !== null && (
        <div className="gym-lightbox" onClick={close}>
          <button type="button" className="gym-lightbox-close" onClick={close} aria-label="Close">
            &times;
          </button>
          <button
            type="button"
            className="gym-lightbox-nav gym-lightbox-prev"
            onClick={(e) => { e.stopPropagation(); showPrev(); }}
            aria-label="Previous photo"
          >
            &#8249;
          </button>
          <img
            src={photos[activeIndex].src}
            alt={photos[activeIndex].alt}
            className="gym-lightbox-image"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            className="gym-lightbox-nav gym-lightbox-next"
            onClick={(e) => { e.stopPropagation(); showNext(); }}
            aria-label="Next photo"
          >
            &#8250;
          </button>
          <p className="gym-lightbox-counter">{activeIndex + 1} / {photos.length}</p>
        </div>
      )}
    </main>
  );
}
